<script lang="ts" setup>
	import ShoppingcartOrderLineOrderInput from '@/components/ShoppingcartOrderLineOrderInput.vue';
	import ShoppingcartSubstitutesRow from '@/components/ShoppingcartSubstitutesRow.vue';
	import ShoppingcartConsignmentSelect from '@/components/ShoppingcartConsignmentSelect.vue';
	import {getProductDetailUrl} from '@/service/order.line.service';
	import {
		isInvalidAmount,
		isSameVarietyAndWeek,
		ProductTypeIcon,
		removeOrderLineFromShoppingCart,
		type ShoppingCartOrderLine
	} from 'dummen-shared';
	import {computed, ref, watch} from 'vue';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';
	import {useI18n} from 'vue-i18n';
	import SplitConsignmentModal from '@/components/modals/SplitConsignmentModal.vue';

	const props = defineProps<{
		orderLine: ShoppingCartOrderLine;
	}>();

	const {t} = useI18n();

	const shoppingCartStore = useShoppingCartStore();

	const showSubstituteRow = ref(false);

	const showSplitConsignmentModal = ref(false);

	const addConsignment = ref(false);

	const amountAvailable = computed((): number => props.orderLine.amountAvailable);

	const isUnknownVariety = computed((): boolean => props.orderLine?.notExistingVariety);

	const allowOverbooking = computed(() => props.orderLine.allowOverbooking);

	const isOverbooking = computed(() => allowOverbooking.value && props.orderLine.amount > amountAvailable.value);

	const noAmountAvailable = computed((): boolean => amountAvailable.value < 1 && !allowOverbooking.value);

	const isConsignmentDisabled = computed(() => isUnknownVariety.value || noAmountAvailable.value);

	const disableSubstitutes = computed(() => {
		return isUnknownVariety.value && !props.orderLine.articleId && !props.orderLine.formId;
	});

	const invalidAmount = computed((): boolean => {
		return isInvalidAmount(
			props.orderLine.amount,
			props.orderLine.packingUnit,
			amountAvailable.value,
			allowOverbooking.value
		)
	});

	/**
	 * Returns the order lines of the same variety as the current order line within the same week.
	 * Used to calculate the sum of all order lines.
	 */
	const orderLinesOfSameVariety = computed(() => {
		const week = props.orderLine.week;
		const year = props.orderLine.year;

		return shoppingCartStore.getOrderLinesForWeek(week, year)
			?.filter((line: ShoppingCartOrderLine) => line && isSameVarietyAndWeek(line, props.orderLine));
	});

	const sumOfAllVarieties = computed(() => {
		if (!orderLinesOfSameVariety.value?.length) {
			return 0;
		}

		return orderLinesOfSameVariety.value
			.map((line: ShoppingCartOrderLine) => line.amount)
			.reduce((prev: number, next: number) => prev + next);
	});

	const invalidTotalVarietyAmount = computed(() => {
		if (props.orderLine.amount != null && props.orderLine.amount <= 0) {
			return true;
		}

		return !allowOverbooking.value && (sumOfAllVarieties.value > amountAvailable.value);
	});

	const tooltipContent = computed(() => {
		if (props.orderLine.allowOverbooking) {
			return undefined;
		}

		if (sumOfAllVarieties.value > amountAvailable.value) {
			return `The sum of the same variety over all consignments (${sumOfAllVarieties.value}) exceeds the available amount of (${amountAvailable.value})`;
		}

		return undefined;
	});

	const getProductImage = (): string => {
		if (props.orderLine?.image) {
			return props.orderLine.image;
		}

		return '/assets/images/build/fallback-product-image.jpg';
	};

	const getDetailUrl = (): string | undefined => getProductDetailUrl(props.orderLine);

	const deleteOrderLine = (): void => removeOrderLineFromShoppingCart(props.orderLine);

	const toggleSubstitutes = (): void => {
		showSubstituteRow.value = !showSubstituteRow.value;
	};

	const openSplitConsignmentModal = (): void => {
		showSplitConsignmentModal.value = true;
	};

	watch(amountAvailable, (amount: number) => {
		if (amount === 0) {
			toggleSubstitutes();
		}
	});
</script>

<template>
	<tr :class="{'cancelled': isUnknownVariety }">
		<td data-column="remove">
			<button type="button" @click="deleteOrderLine">
				<svg class="icon-modal-close" height="10" width="10">
					<use xlink:href="/assets/images/build/svg-sprite.min.svg#modal-close"></use>
				</svg>
			</button>
		</td>

		<td data-column="image"
		    headers="column-image">
			<a :href="getDetailUrl()" target="_blank">
				<img :alt="orderLine.articleId"
				     :src="getProductImage()"
				     height="48"
				     sizes="(min-width: 640px) 48px, 65px">
			</a>
		</td>

		<td>
			<h4>
				<a :href="getDetailUrl()" target="_blank">
					<ProductTypeIcon :type="orderLine.type" />
					<span v-text="orderLine.articleName"></span>
				</a>
			</h4>
		</td>

		<td>
			<a :href="getDetailUrl()" target="_blank">
				<span v-text="orderLine.articleId"></span>
			</a>
		</td>

		<td>
			<div v-text="orderLine.groupName"></div>
		</td>

		<td>
			<strong v-text="orderLine.formName"></strong>
		</td>

		<td>
			<button v-if="!orderLine.consignment && !addConsignment"
			        class="button button--white add-consignment-button"
			        type="button"
			        @click="addConsignment = true">

				{{ t('shoppingcart.addConsignment.text') }}
			</button>

			<shoppingcart-consignment-select v-else
			                                 :is-disabled="isConsignmentDisabled"
			                                 :orderLine="orderLine" />
		</td>

		<td data-column="split">
			<button :disabled="noAmountAvailable || isUnknownVariety"
			        type="button"
			        @click="openSplitConsignmentModal">

				<svg class="icon-split" height="14" width="14">
					<use xlink:href="/assets/images/build/svg-sprite.min.svg#split-consignment"></use>
				</svg>
			</button>
		</td>

		<td data-column="substitutes">
			<button :disabled="disableSubstitutes"
			        type="button"
			        @click="toggleSubstitutes">

				<svg class="icon-substitute" height="30" width="30">
					<use xlink:href="/assets/images/build/svg-sprite.min.svg#icon-substitute"></use>
				</svg>
			</button>
		</td>

		<td class="shoppingcart-order-line-row__amount">
			<shoppingcart-order-line-order-input :available="amountAvailable"
			                                     :invalid="invalidAmount || invalidTotalVarietyAmount"
			                                     :order-line="orderLine"
			                                     :packing-unit="orderLine.packingUnit"
			                                     :tooltip="tooltipContent"
			                                     :week="orderLine.week"
			                                     :year="orderLine.year" />
		</td>

		<td class="shoppingcart-order-line-row__amount-available">
			<span>/ {{ amountAvailable }}</span>
		</td>

		<td>
			<svg v-if="invalidAmount || invalidTotalVarietyAmount || isOverbooking"
			     class="icon-notice" height="16" width="16">
				<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#notice"></use>
			</svg>
		</td>
	</tr>

	<shoppingcart-substitutes-row v-if="showSubstituteRow"
	                              :columns="10"
	                              :orderLine="orderLine" />

	<teleport to="#shoppingcart-modal-container">
		<split-consignment-modal v-if="showSplitConsignmentModal"
		                         :amount="orderLine.amount"
		                         :order-line="orderLine"
		                         @close="showSplitConsignmentModal = false" />
	</teleport>
</template>

<style lang="scss" scoped>
	@import '../../../../../../src/main/webapp/template/assets/css/source/settings';

	.add-consignment-button {
		width: 100%;
		font-size: 9px;

		@media #{$mq-large-only} {
			font-size: 11px;
		}
	}
</style>
